import {
  Firestore,
  WriteBatch,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore/lite";
import { firebaseDb } from "../firebase/client-app";
import {
  DbBusinessCategory,
  DbBusinessService,
  DbBusinessServiceCategory,
  DbServiceCategoryStatus,
  DbServiceStatus,
} from "./models";
import { createChunks } from "../util/array-extensions";
import { businessService } from "../business/service";

export class ServiceCategoryService {
  private categoriesRef(firestore: Firestore) {
    return collection(firestore, "business_categories");
  }

  private servicesRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_services",
    );
  }

  private servicesCategoryRef(firestore: Firestore, businessId: string) {
    return collection(
      firestore,
      "businesses/" + businessId + "/business_service_categories",
    );
  }

  generateServiceCategoryId(firestore: Firestore, businessId: string): string {
    return doc(this.servicesCategoryRef(firestore, businessId)).id;
  }

  generateServiceId(firestore: Firestore, businessId: string): string {
    return doc(this.servicesRef(firestore, businessId)).id;
  }

  async getService(
    businessId: string,
    serviceId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessService | null> {
    const service = await getDoc(
      doc(this.servicesRef(firestore, businessId), serviceId),
    );
    if (!service.exists()) {
      return null;
    }
    return service.data() as DbBusinessService;
  }

  async getServices(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessService[]> {
    const servicesSnapshot = await getDocs(
      query(
        this.servicesRef(firestore, businessId),
        where("service_status", "==", DbServiceStatus.enable),
      ),
    );
    const services = servicesSnapshot.docs.map(
      (doc) => doc.data() as DbBusinessService,
    );
    const filteredServices = services.filter(
      (service) => service.category_id === null || !service.category_id,
    );
    filteredServices.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    return filteredServices;
  }

  async getAllServices(
    businessId: string,
    enabledOnly = true,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessService[]> {
    try {
      const servicesSnapshot = await getDocs(
        query(
          this.servicesRef(firestore, businessId),
          enabledOnly
            ? where("service_status", "==", DbServiceStatus.enable)
            : where("service_status", "!=", DbServiceStatus.deleted),
        ),
      );
      const services = servicesSnapshot.docs.map(
        (doc) => doc.data() as DbBusinessService,
      );
      return services;
    } catch (error: any) {
      throw error;
    }
  }

  async getServicesById(
    ids: { businessId: string; serviceId: string }[],
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessService[]> {
    const services = await Promise.all(
      ids.map((id) => this.getService(id.businessId, id.serviceId, firestore)),
    );
    return services.filter(
      (service) => service !== null,
    ) as DbBusinessService[];
  }

  async getServicesByIds(
    ids: string[],
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessService[]> {
    try {
      const chunks = ids.length > 30 ? createChunks(ids, 30) : [ids];
      // Fetch users in parallel for each chunk
      const servicesPromises = chunks.map(async (chunk) => {
        const serviceSnapshot = await getDocs(
          query(
            this.servicesRef(firestore, businessId),
            where("id", "in", chunk),
          ),
        );
        return serviceSnapshot.docs.map(
          (doc) => doc.data() as DbBusinessService,
        );
      });
      const services = await Promise.all(servicesPromises);
      return services.flat();
    } catch (error: any) {
      throw error;
    }
  }

  async getCategoriesAndServices(
    businessId: string,
    enabledOnly: boolean = true,
    firestore: Firestore = firebaseDb,
  ): Promise<{
    services: DbBusinessService[];
    categories: DbBusinessServiceCategory[];
  }> {
    const [servicesSnapshot, servicesCategorySnapshot] = await Promise.all([
      getDocs(
        query(
          this.servicesRef(firestore, businessId),
          enabledOnly
            ? where("service_status", "==", DbServiceStatus.enable)
            : where("service_status", "!=", DbServiceStatus.deleted),
        ),
      ),
      getDocs(
        query(
          this.servicesCategoryRef(firestore, businessId),
          where("category_status", "==", DbServiceCategoryStatus.enable),
        ),
      ),
    ]);

    const services = servicesSnapshot.docs.map(
      (doc) => doc.data() as DbBusinessService,
    );
    const filteredServices = services.filter(
      (service) => service.category_id === null || !service.category_id,
    );
    filteredServices.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

    const categories = servicesCategorySnapshot.docs.map(
      (doc) => doc.data() as DbBusinessServiceCategory,
    );
    categories.sort((a, b) => a.index - b.index);
    categories.forEach((category) => {
      category.services = category.services
        .map((categoryService: any) => {
          return (
            services.find(
              (service) => service.id === categoryService.service_id,
            ) || null
          );
        })
        .filter((service) => service !== null);
    });
    return { services: filteredServices, categories };
  }

  async getCategoryServices(
    businessId: string,
    categoryId: string,
    enabledOnly: boolean = true,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessServiceCategory | null> {
    const servicesCategory = await getDoc(
      doc(this.servicesCategoryRef(firestore, businessId), categoryId),
    );

    if (!servicesCategory.exists()) {
      return null;
    }

    const category = servicesCategory.data();
    const serviceIds: string[] = category.services
      ? category.services.map(
          (service: { service_id: any }) => service.service_id,
        )
      : [];

    if (serviceIds.length == 0) {
      category.services = [];
      return category as DbBusinessServiceCategory;
    }

    const chunks =
      serviceIds.length > 30 ? createChunks(serviceIds, 30) : [serviceIds];

    const servicePromises = chunks.map(async (chunk) => {
      const queryConstraints = [where("id", "in", chunk)];
      if (enabledOnly) {
        queryConstraints.push(
          where("service_status", "==", DbServiceStatus.enable),
        );
      }
      const serviceSnapshot = await getDocs(
        query(this.servicesRef(firestore, businessId), ...queryConstraints),
      );
      return serviceSnapshot.docs.reduce(
        (acc, doc) => {
          const serviceData = doc.data() as DbBusinessService;
          acc[serviceData.id] = serviceData;
          return acc;
        },
        {} as Record<string, DbBusinessService>,
      );
    });

    const servicesArray = await Promise.all(servicePromises);
    const servicesMap = servicesArray.reduce((acc, chunkServices) => {
      return { ...acc, ...chunkServices };
    }, {});

    category.services =
      category.services
        ?.map((categoryService: any) => {
          return servicesMap[categoryService.service_id] || null;
        })
        .filter(Boolean) || [];

    return category as DbBusinessServiceCategory;
  }

  async removeBusinessService(
    businessId: string,
    serviceId: string,
    firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(doc(this.servicesRef(firestore, businessId), serviceId), {
      service_status: DbServiceStatus.deleted,
    });
    await businessService.updateHasServiceState(businessId);
  }

  async removeBusinessServiceCategory(
    businessId: string,
    servicesCategory: DbBusinessServiceCategory,
    firestore = firebaseDb,
  ): Promise<void> {
    const serviceUpdatePromises = servicesCategory.services.map((service) =>
      updateDoc(doc(this.servicesRef(firestore, businessId), service.id), {
        category_id: null,
      }),
    );
    const categoryStatusUpdatePromise = updateDoc(
      doc(this.servicesCategoryRef(firestore, businessId), servicesCategory.id),
      {
        category_status: DbServiceCategoryStatus.deleted,
      },
    );
    await Promise.all([...serviceUpdatePromises, categoryStatusUpdatePromise]);
  }

  async updateCategoryServices(
    businessId: string,
    categoryId: string,
    services: {
      service_id: string;
      service_index: number;
    }[],
    firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(
      doc(this.servicesCategoryRef(firestore, businessId), categoryId),
      { services: services },
    );
  }

  async updateServiceCategoryDetails(
    businessId: string,
    categoryId: string,
    serviceCategoryDetails: Pick<DbBusinessServiceCategory, "name" | "image">,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(
      doc(this.servicesCategoryRef(firestore, businessId), categoryId),
      serviceCategoryDetails,
    );
  }

  async addServiceCategory(
    businessId: string,
    serviceCategory: Omit<DbBusinessServiceCategory, "id">,
    firestore = firebaseDb,
  ): Promise<DbBusinessServiceCategory> {
    const categoryId = this.generateServiceCategoryId(firestore, businessId);
    const categoryData = {
      ...serviceCategory,
      id: categoryId,
    };
    await setDoc(
      doc(this.servicesCategoryRef(firestore, businessId), categoryId),
      categoryData,
    );
    return categoryData;
  }

  async setServices(
    businessId: string,
    services: DbBusinessService[],
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    const batch: WriteBatch = writeBatch(firestore);
    services.forEach((service) => {
      batch.update(doc(this.servicesRef(firestore, businessId), service.id), {
        ...service,
      });
    });
    await batch.commit();
  }

  async setServiceCategories(
    businessId: string,
    serviceCategories: DbBusinessServiceCategory[],
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    const batch: WriteBatch = writeBatch(firestore);
    serviceCategories.forEach((serviceCategory) => {
      const updatedServices = serviceCategory.services.map((service, idx) => ({
        service_id: service.id,
        service_index: idx,
      }));
      batch.update(
        doc(
          this.servicesCategoryRef(firestore, businessId),
          serviceCategory.id,
        ),
        {
          ...serviceCategory,
          services: updatedServices,
        },
      );
    });
    await batch.commit();
  }

  async getServiceCategories(
    businessId: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessServiceCategory[]> {
    const categoriesDocs = await getDocs(
      query(
        this.servicesCategoryRef(firestore, businessId),
        where("category_status", "==", DbServiceCategoryStatus.enable),
      ),
    );
    const categories = categoriesDocs.docs.map(
      (doc) => doc.data() as DbBusinessServiceCategory,
    );
    return categories.sort((a, b) => a.index - b.index);
  }

  async addServiceToCategory(
    businessId: string,
    categoryId: string,
    service: {
      service_id: string;
      service_index: number;
    },
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(
      doc(this.servicesCategoryRef(firestore, businessId), categoryId),
      {
        services: arrayUnion(service),
      },
    );
    await businessService.updateHasServiceState(businessId);
  }

  async updateService(
    businessId: string,
    serviceId: string,
    service: DbBusinessService,
    firestore: Firestore = firebaseDb,
  ): Promise<void> {
    await updateDoc(doc(this.servicesRef(firestore, businessId), serviceId), {
      ...service,
    });
    await businessService.updateHasServiceState(businessId);
  }

  async addService(
    businessId: string,
    service: Omit<DbBusinessService, "id">,
    firestore = firebaseDb,
  ): Promise<DbBusinessService> {
    const serviceId = this.generateServiceId(firestore, businessId);
    const serviceData = {
      ...service,
      id: serviceId,
    };
    await setDoc(
      doc(this.servicesRef(firestore, businessId), serviceId),
      serviceData,
    );
    await businessService.updateHasServiceState(businessId);
    return serviceData;
  }

  async getCategoryById(
    id: string,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessCategory | null> {
    const category = await getDoc(doc(this.categoriesRef(firestore), id));
    if (!category.exists()) {
      return null;
    }
    return category.data() as DbBusinessCategory;
  }

  async getCategories(
    activeOnly: boolean = false,
    firestore: Firestore = firebaseDb,
  ): Promise<DbBusinessCategory[]> {
    const categoriesQuery = query(
      this.categoriesRef(firestore),
      orderBy("index"),
    );
    const categories = await getDocs(categoriesQuery);
    const categoryData = categories.docs.map(
      (doc) => doc.data() as DbBusinessCategory,
    );

    // Filter by active field client-side to avoid Firestore composite index requirement
    if (activeOnly) {
      return categoryData.filter((category) => category.active === true);
    }

    return categoryData;
  }
}

export const serviceCategoryService = new ServiceCategoryService();
